import type { ReactNode } from 'react'
import { formatAmount } from '@/lib/utils/formatters'
import { PAYMENT_STATUS_LABELS } from '../constants/pages/defaultPaymentLogsPage'
import type { DefaultPaymentLogRow } from '../types/api'
import type { DefaultPaymentLogsColumnId } from '../types/listColumns'
import type { StatusTone } from '../types/pages'
import reportStyles from '../styles/reportsTable.module.css'
import { formatReportDate } from './reportDateDisplay'

function formatPaymentDate(value: string | undefined): string {
  return formatReportDate(value)
}

function formatContractDate(value: string | undefined): string {
  return formatReportDate(value, '')
}

function residentDisplayId(id: number | undefined): string {
  if (id == null || Number.isNaN(Number(id))) return ''
  return String(id).padStart(8, '0')
}

function claimStatusDisplay(status: string | undefined): string {
  if (!status) return '—'
  if (status === 'External Review') return 'Level 3 Review'
  return status
}

function statusPillClass(tone: StatusTone): string {
  const toneClass =
    tone === 'success'
      ? reportStyles.statusPillSuccess
      : tone === 'warning'
        ? reportStyles.statusPillWarning
        : tone === 'info'
          ? reportStyles.statusPillInfo
          : reportStyles.statusPillDefault
  return `${reportStyles.statusPill} ${toneClass}`
}

function paymentStatusTone(status: string | undefined): StatusTone {
  const raw = String(status ?? '').toLowerCase()
  if (raw.includes('complete') || raw.includes('recovered')) return 'success'
  if (raw.includes('pending') || raw.includes('hold')) return 'warning'
  return 'default'
}

function claimStatusTone(status: string | undefined): StatusTone {
  const raw = String(status ?? '').toLowerCase()
  if (raw.includes('paid')) return 'success'
  if (raw.includes('pending')) return 'warning'
  if (raw.includes('review')) return 'info'
  return 'default'
}

/** Angular truncates notes at 20 chars + .. */
function paymentNotesPreview(raw: string | undefined): string {
  if (!raw) return ''
  const s = String(raw)
  return s.length > 20 ? `${s.slice(0, 20)}..` : s
}

export type DefaultPaymentLogCellContext = {
  onOpenResident: (applicantId: number) => void
}

export function renderDefaultPaymentLogCell(
  columnId: DefaultPaymentLogsColumnId,
  row: DefaultPaymentLogRow,
  context: DefaultPaymentLogCellContext,
): ReactNode {
  switch (columnId) {
    case 'payment_log_id':
      return row.payment_log_id ?? '—'
    case 'company_name':
      return row.company_name ?? '—'
    case 'property_name':
      return row.property_name ?? '—'
    case 'fk_applicant_id': {
      const aid =
        row.fk_applicant_id != null ? Number(row.fk_applicant_id) : NaN
      if (!Number.isFinite(aid) || aid <= 0) return '—'
      return (
        <button
          type="button"
          data-testid={`default-payment-logs-resident-${aid}`}
          title="Open reimbursements (defaults) for this resident"
          className="cursor-pointer text-left font-mono text-xs text-[rgb(21_151_255)] underline decoration-[rgb(21_151_255)]/40 underline-offset-2 hover:text-[#0d7acc] hover:decoration-[#0d7acc]"
          onClick={() => context.onOpenResident(aid)}
        >
          {residentDisplayId(row.fk_applicant_id)}
        </button>
      )
    }
    case 'first_name':
      return (
        [row.first_name, row.last_name].filter(Boolean).join(' ') || '—'
      )
    case 'contract_start_date':
      return formatContractDate(row.contract_start_date) || '—'
    case 'payment_notes':
      return row.payment_notes ? (
        <>
          {paymentNotesPreview(row.payment_notes)}
          {row.special_note === 'COVID_19_YES' ? (
            <>
              <br />
              <span className="text-slate-500">Covid_19</span>
            </>
          ) : null}
        </>
      ) : row.special_note === 'COVID_19_YES' ? (
        <span className="text-slate-500">Covid_19</span>
      ) : (
        '—'
      )
    case 'payment_source':
      return row.payment_source ?? '—'
    case 'auth_code':
      return row.auth_code ?? '—'
    case 'vintage_year':
      return row.vintage_year ?? '—'
    case 'payment_amount':
      return formatAmount(row.payment_amount)
    case 'created_at':
      return formatPaymentDate(row.created_at)
    case 'payment_status':
      return row.payment_status ? (
        <span className={statusPillClass(paymentStatusTone(row.payment_status))}>
          {PAYMENT_STATUS_LABELS[row.payment_status] ?? row.payment_status}
        </span>
      ) : (
        '—'
      )
    case 'claim_status':
      return row.claim_status ? (
        <span className={statusPillClass(claimStatusTone(row.claim_status))}>
          {claimStatusDisplay(row.claim_status)}
        </span>
      ) : (
        '—'
      )
    case 'payment_verified':
      return row.payment_verified ?? '—'
    case 'pay_to':
      return row.pay_to ?? '—'
    default:
      return '—'
  }
}

export function defaultPaymentLogCellClassName(
  columnId: DefaultPaymentLogsColumnId,
): string | undefined {
  switch (columnId) {
    case 'payment_log_id':
      return 'font-mono text-xs tabular-nums whitespace-nowrap'
    case 'company_name':
    case 'property_name':
      return 'min-w-[11rem] whitespace-normal font-medium'
    case 'fk_applicant_id':
      return 'whitespace-nowrap font-mono text-xs'
    case 'first_name':
      return 'min-w-[10rem] whitespace-normal'
    case 'contract_start_date':
      return 'whitespace-nowrap'
    case 'payment_notes':
      return 'min-w-[8rem] text-xs leading-snug'
    case 'payment_source':
    case 'auth_code':
    case 'created_at':
      return 'whitespace-nowrap font-mono text-xs'
    case 'vintage_year':
    case 'payment_status':
    case 'claim_status':
    case 'payment_verified':
    case 'pay_to':
      return 'whitespace-nowrap'
    case 'payment_amount':
      return 'whitespace-nowrap tabular-nums font-semibold'
    default:
      return undefined
  }
}
